import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import {
  fetchAnnualGoals,
  upsertAnnualGoals,
  fetchMonthlyPerformance,
  upsertMonthlyPerformance,
  fetchMajorEvents,
  fetchActiveLeagues,
  fetchTicketStats,
  formatBreakdownCurrency,
} from "./goals.api";
import type { BreakdownTotals } from "./goals.api";
import { emptyBreakdownTotals } from "./goals.breakdown";
import {
  DashboardDatePeriod,
  DASHBOARD_DATE_PRESETS,
  formatDashboardDateRangeLabel,
  getDashboardDateRange,
  getDashboardDateRangeQueryBounds,
  isDashboardDateFilterActive,
} from "../Dashboard/utils/dashboardDateRange";

import styles from "./goals.module.css";
import SearchIcon from "@mui/icons-material/Search";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Calendar } from "lucide-react";
import { globalContext } from "../../Provider/AppProvider";
import { roles } from "../../helper";

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * 📊 GOALS PAGE - REVENUE & PROFIT CALCULATION
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Total Revenue = SUM(tp_logitix_amount) — full ticket sale price per placement
 * QA Profit     = SUM(tp_quick_cut_amount) — persisted at mark-as-sold (Admin Dashboard)
 *
 * Per-sale breakdown (written at mark-as-sold):
 *   Total sale     → tp_logitix_amount
 *   User payout    → tp_sold_amount
 *   VL fee         → total sale − user payout − QA gross (residual so parts sum)
 *   QA gross cut   → tp_quick_cut_amount (= list × (client% − marketplace%))
 *   Affiliate/rep  → tp_referral_details + sales rep % of quick cut (tracked elsewhere)
 * ═══════════════════════════════════════════════════════════════════════════════
 */

// Define types
type ComparisonData = {
  revenue: string;
  profit: string;
  tickets: string;
  percent: string;
};

type MonthData = {
  revenue: string;
  profit: string;
  tickets: string;
  leagues: string[];
  events: string[];
  goal: string;
  profitGoal: string;
  comparison?: ComparisonData;
};

type MonthlyData = {
  [key: string]: MonthData;
};

const MONTH_KEYS = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december"
];

// ===== UTILS =====
const toNumber = (val: any): number => Number(val) || 0;

const getPercentage = (actual: number, goal: number): number => {
  if (!goal) return 0;
  return Number(((actual / goal) * 100).toFixed(1));
};

const getPerformanceStatus = (actual: number, goal: number) => {
  if (!goal) return 'no-goal';
  if (actual > goal) return 'overachieved';
  if (actual === goal) return 'achieved';
  if (actual > goal * 0.5) return 'on-track';
  return 'underperform';
};

const getMonthlyGoal = (monthlyGoal: number, annualGoal: number) => {
  if (monthlyGoal && monthlyGoal > 0) return monthlyGoal;
  return 0;
};

const getYoYChange = (current: number, previous: number) => {
  const diff = current - previous;
  if (previous === 0) {
    return {
      difference: diff,
      percentage: current > 0 ? 100 : 0,
      type: current > 0 ? 'profit' : 'neutral' as 'profit' | 'loss' | 'neutral',
    };
  }
  const rawPct = Math.abs((diff / previous) * 100);
  const pct = Math.min(rawPct, 100);
  return {
    difference: diff,
    percentage: Number(pct.toFixed(1)),
    type: (diff > 0 ? 'profit' : diff < 0 ? 'loss' : 'neutral') as 'profit' | 'loss' | 'neutral',
  };
};

const getColor = (status: string) => {
  switch (status) {
    case 'overachieved': return 'green';
    case 'achieved': return '#16a34a';
    case 'on-track': return '#f59e0b';
    case 'underperform': return '#dc2626';
    default: return '#6b7280';
  }
};

// Function to get progress bar color based on percentage
const getProgressBarColor = (percentage: number, type: 'revenue' | 'profit') => {
  if (type === 'profit') {
    if (percentage >= 100) return '#22c55e'; // Green - Goal achieved/exceeded
    if (percentage >= 75) return '#f59e0b'; // Orange - On track
    if (percentage >= 50) return '#f97316'; // Orange - Getting there
    if (percentage >= 25) return '#ef4444'; // Red - Behind
    return '#dc2626'; // Dark Red - Significantly behind
  } else {
    if (percentage >= 100) return '#22c55e';
    if (percentage >= 75) return '#3b82f6';
    if (percentage >= 50) return '#6366f1';
    if (percentage >= 25) return '#8b5cf6';
    return '#a855f7';
  }
};

// League color palette
const LEAGUE_COLOR_MAP: { [key: string]: string } = {
  NBA: '#F97316', MLB: '#2563EB', NFL: '#22C55E', NHL: '#64748B',
  MLS: '#E11D48', 'NCAA Football': '#A21CAF', NCAAFB: '#A21CAF',
  "NCAA Basketball Men's": '#F59E42', NCAAM: '#F59E42', NCAABB: '#F59E42',
  "NCAA Basketball Women's": '#0EA5E9', NCAAW: '#0EA5E9', NCAASW: '#0EA5E9',
  WNBA: '#F43F5E',
  "NCAA Baseball": '#10B981', NCAASB: '#10B981',
  NCAAH: '#7C3AED',
  Concerts: '#8B5CF6', Events: '#FBBF24', Rodeo: '#A16207', Comedy: '#EC4899',
  Baseball: '#1D4ED8', Boxing: '#DC2626', Golf: '#16A34A',
  NASCAR: '#EA580C', Tennis: '#0284C7', Theatre: '#7C3AED',
  UFC: '#B91C1C', WWE: '#DC2626',
};

const LEAGUE_COLOR_PALETTE = [
  '#F97316','#2563EB','#22C55E','#A21CAF','#F59E42','#0EA5E9',
  '#F43F5E','#10B981','#8B5CF6','#E11D48','#FBBF24','#EC4899',
  '#64748B','#A16207','#1D4ED8','#DC2626','#16A34A','#EA580C',
  '#0284C7','#7C3AED','#B91C1C',
];

const getLeagueColor = (league: string, idx: number): string =>
  LEAGUE_COLOR_MAP[league] || LEAGUE_COLOR_PALETTE[idx % LEAGUE_COLOR_PALETTE.length];

const Goals: React.FC = () => {
  const { globalState } = useContext<any>(globalContext);
  const userRole = globalState?.profile?.userRole || localStorage.getItem("x-hasura-user-role") || "";
  const isMarketingUser = false;
  // ✅ FIXED: Dark mode with localStorage persistence
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // Check data-theme attribute
    const dataTheme = document.documentElement.getAttribute('data-theme');
    if (dataTheme) {
      return dataTheme === 'dark';
    }
    // Fall back to system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  
  // Apply dark mode to document and save to localStorage
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);
  
  // Optional: Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't manually set a preference
      if (!localStorage.getItem('theme')) {
        setIsDarkMode(e.matches);
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState<"buy" | "sell">("sell");
  const [showEditGoalsPopup, setShowEditGoalsPopup] = useState(false);
  const [editMonth, setEditMonth] = useState<string | null>(null);
  const [editMonthForm, setEditMonthForm] = useState({ 
    actualRevenue: '', 
    actualProfit: '', 
    revenueGoal: '', 
    profitGoal: '', 
    leagues: [] as string[], 
    events: [] as string[], 
    eventInput: '' 
  });
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [dashboardDatePeriod, setDashboardDatePeriod] = useState<DashboardDatePeriod>('All');
  const [customDashboardStart, setCustomDashboardStart] = useState('');
  const [customDashboardEnd, setCustomDashboardEnd] = useState('');
  const [showDashboardDatePicker, setShowDashboardDatePicker] = useState(false);
  const dashboardDateRef = useRef<HTMLDivElement | null>(null);

  const dashboardDateRange = getDashboardDateRange(dashboardDatePeriod, customDashboardStart, customDashboardEnd);
  const dashboardDateFilterActive = isDashboardDateFilterActive(dashboardDatePeriod) && !!dashboardDateRange;
  const dashboardDateLabel = formatDashboardDateRangeLabel(dashboardDatePeriod, customDashboardStart, customDashboardEnd);

  const [annualGoals, setAnnualGoals] = useState({
    totalRevenue: "0",
    qaProfit: "0",
    totalTickets: "0",
    revenueAchieved: "0",
    profitAchieved: "0",
    ticketsAchieved: "0",
    revenuePercentage: "0",
    profitPercentage: "0",
    ticketsPercentage: "0"
  });

  const [monthlyData, setMonthlyData] = useState<MonthlyData>({});
  const [globalLeagues, setGlobalLeagues] = useState<string[]>([]);
  const [majorEventsList, setMajorEventsList] = useState<any[]>([]);
  const [prevAnnual, setPrevAnnual] = useState<{ revenue: number; profit: number; revenue_goal: number; profit_goal: number } | null>(null);
  const [prevMonthlyData, setPrevMonthlyData] = useState<MonthlyData>({});
  const [annualBreakdown, setAnnualBreakdown] = useState<BreakdownTotals>(emptyBreakdownTotals());
  const [prevAnnualBreakdown, setPrevAnnualBreakdown] = useState<BreakdownTotals>(emptyBreakdownTotals());
  const [monthlyBreakdownStats, setMonthlyBreakdownStats] = useState<Array<{ month: number; qaNet: number }>>([]);

  const loadData = useCallback(async (year: string, dateRange?: { startDate: string; endDate: string }) => {
    setLoading(true);
    setError(null);
    const prevYear = String(Number(year) - 1);
    try {
      const [annual, monthly, events, leaguesList, prevAnnualData, prevMonthly, ticketStats, prevTicketStats] = await Promise.all([
        fetchAnnualGoals(Number(year)),
        fetchMonthlyPerformance(Number(year)),
        fetchMajorEvents(),
        fetchActiveLeagues(),
        fetchAnnualGoals(Number(prevYear)),
        fetchMonthlyPerformance(Number(prevYear)),
        fetchTicketStats(Number(year), dateRange),
        fetchTicketStats(Number(prevYear)),
      ]);

      const currentMonthly = ticketStats.monthly;
      const currentAnnual = ticketStats.annual;
      const prevMonthlyStats = prevTicketStats.monthly;
      const prevAnnualStats = prevTicketStats.annual;

      setAnnualBreakdown(currentAnnual);
      setPrevAnnualBreakdown(prevAnnualStats);
      setMonthlyBreakdownStats(
        currentMonthly.map((m) => ({ month: m.month, qaNet: m.qaNet })),
      );

      const prevMonthlyArray: any[] = Array.isArray(prevMonthly) ? prevMonthly : [];
      setPrevAnnual({
        revenue: prevAnnualStats.revenue,
        profit: prevAnnualStats.profit,
        revenue_goal: toNumber(prevAnnualData?.revenue_goal),
        profit_goal: toNumber(prevAnnualData?.profit_goal),
      });

      const revGoal = toNumber(annual?.revenue_goal);
      const profGoal = toNumber(annual?.profit_goal);
      const totalTickets = currentAnnual.tickets;
      const revenuePercentage = getPercentage(currentAnnual.revenue, revGoal);
      const profitPercentage = getPercentage(currentAnnual.profit, profGoal);
      const ticketsPercentage = 0;
      
      setAnnualGoals({
        totalRevenue: revGoal.toLocaleString('en-US'),
        qaProfit: profGoal.toLocaleString('en-US'),
        totalTickets: totalTickets.toString(),
        revenueAchieved: currentAnnual.revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        profitAchieved: currentAnnual.profit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        ticketsAchieved: totalTickets.toString(),
        revenuePercentage: revenuePercentage.toString(),
        profitPercentage: profitPercentage.toString(),
        ticketsPercentage: ticketsPercentage.toString(),
      });

      const monthlyArray: any[] = Array.isArray(monthly) ? monthly : [];
      const eventsArray: any[] = Array.isArray(events) ? events : [];
      setMajorEventsList(eventsArray);
      
      const mapped: MonthlyData = {};
      MONTH_KEYS.forEach((key, idx) => {
        const m = monthlyArray.find((r: any) => r.month === idx + 1);
        const ts = currentMonthly.find((r) => r.month === idx + 1);
        mapped[key] = {
          revenue: ts ? ts.revenue.toFixed(2) : "0",
          profit: ts ? ts.profit.toFixed(2) : "0",
          tickets: ts ? ts.tickets.toString() : "0",
          leagues: Array.isArray(m?.leagues) ? m.leagues : [],
          events: Array.isArray(m?.events) ? m.events : [],
          goal: m?.goal_amount?.toString() || "0",
          profitGoal: m?.profit_goal?.toString() || "0",
        };
      });
      setMonthlyData(mapped);

      const prevMapped: MonthlyData = {};
      MONTH_KEYS.forEach((key, idx) => {
        const m = prevMonthlyArray.find((r: any) => r.month === idx + 1);
        const ts = prevMonthlyStats.find((r) => r.month === idx + 1);
        prevMapped[key] = {
          revenue: ts ? ts.revenue.toFixed(2) : "0",
          profit: ts ? ts.profit.toFixed(2) : "0",
          tickets: ts ? ts.tickets.toString() : "0",
          leagues: [],
          events: [],
          goal: m?.goal_amount?.toString() || "0",
          profitGoal: m?.profit_goal?.toString() || "0",
        };
      });
      setPrevMonthlyData(prevMapped);

      if (Array.isArray(leaguesList)) {
        setGlobalLeagues(leaguesList.map((l: any) => (typeof l === 'string' ? l : l.l_name || l.name)));
      }
    } catch (e: any) {
      setError(e.message || "Failed to load goals");
      console.error("Error loading data:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(selectedYear);
  }, [selectedYear, loadData]);

  // Re-load data when date filter changes
  useEffect(() => {
    const dashboardDateRange = getDashboardDateRange(dashboardDatePeriod, customDashboardStart, customDashboardEnd);
    const dateRange = dashboardDateRange
      ? getDashboardDateRangeQueryBounds(dashboardDateRange.startDate, dashboardDateRange.endDate)
      : undefined;
    
    if (dateRange) {
      loadData(selectedYear, { startDate: dateRange.startDateTime, endDate: dateRange.endDateTime });
    } else if (dashboardDatePeriod === 'All') {
      // If 'All' is selected, reload without date range constraint
      loadData(selectedYear);
    }
  }, [dashboardDatePeriod, customDashboardStart, customDashboardEnd, selectedYear, loadData]);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dashboardDateRef.current && !dashboardDateRef.current.contains(event.target as Node)) {
        setShowDashboardDatePicker(false);
      }
    };

    if (showDashboardDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDashboardDatePicker]);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const leagues = globalLeagues;

  const handleRefresh = () => {
    loadData(selectedYear);
    window.location.reload();
  };

  const handleDashboardDatePreset = (period: DashboardDatePeriod) => {
    setDashboardDatePeriod(period);
    if (period !== 'Custom') {
      setShowDashboardDatePicker(false);
    }
  };

  const handleClearDashboardDateFilter = () => {
    setDashboardDatePeriod('All');
    setCustomDashboardStart('');
    setCustomDashboardEnd('');
    setShowDashboardDatePicker(false);
  };


  const getFilteredMonths = useCallback(() => {
    if (!searchText.trim()) return months;
    
    const query = searchText.toLowerCase().trim();
    return months.filter((month) => {
      const monthKey = getMonthKey(month);
      const data = monthlyData[monthKey];
      if (!data) return false;
      
      if (month.toLowerCase().includes(query)) return true;
      if (data.leagues.some(league => league.toLowerCase().includes(query))) return true;
      if (data.events.some(event => event.toLowerCase().includes(query))) return true;
      if (data.revenue.includes(query) || data.profit.includes(query) || data.tickets.includes(query)) return true;
      
      return false;
    });
  }, [searchText, monthlyData, months]);

  const handleSaveAnnualGoals = async () => {
   
    
    const totalRevenueNum = Number(annualGoals.totalRevenue.replace(/,/g, '')) || 0;
    const totalProfitNum = Number(annualGoals.qaProfit.replace(/,/g, '')) || 0;
    if (isNaN(totalRevenueNum) || isNaN(totalProfitNum)) {
      setError("Annual goals must be valid numbers");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await upsertAnnualGoals(Number(selectedYear), totalRevenueNum, totalProfitNum);
      setShowEditGoalsPopup(false);
      await loadData(selectedYear);
    } catch (e: any) {
      setError(e.message || "Failed to save annual goals");
    } finally {
      setLoading(false);
    }
  };

  const handleEditMonth = (month: string) => {

    const monthKey = getMonthKey(month);
    const data = monthlyData[monthKey];
    setEditMonth(month);
    setEditMonthForm({
      actualRevenue: data.revenue,
      actualProfit: data.profit,
      revenueGoal: data.goal,
      profitGoal: data.profitGoal,
      leagues: data.leagues || [],
      events: data.events || [],
      eventInput: '',
    });
  };

  const handleEditMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditMonthForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddEvent = () => {
    if (isMarketingUser) return;
    const name = editMonthForm.eventInput.trim();
    if (!name) return;
    setEditMonthForm(prev => ({
      ...prev,
      events: prev.events.includes(name) ? prev.events : [...prev.events, name],
      eventInput: '',
    }));
  };

  const handleRemoveEvent = (event: string) => {
    if (isMarketingUser) return;
    setEditMonthForm(prev => ({ ...prev, events: prev.events.filter(e => e !== event) }));
  };

  const handleEditMonthLeagueToggle = (league: string) => {
    if (isMarketingUser) return;
    setEditMonthForm((prev) => {
      const exists = prev.leagues.includes(league);
      return {
        ...prev,
        leagues: exists ? prev.leagues.filter(l => l !== league) : [...prev.leagues, league],
      };
    });
  };

  const handleSaveMonthGoals = async (month: string) => {
    if (isMarketingUser) {
      setError("You don't have permission to edit monthly goals");
      return;
    }
    const revenueGoalNum = editMonthForm.revenueGoal.trim() === '' ? 0 : Number(editMonthForm.revenueGoal);
    const profitGoalNum = editMonthForm.profitGoal.trim() === '' ? 0 : Number(editMonthForm.profitGoal);
    
    if (isNaN(revenueGoalNum) || isNaN(profitGoalNum)) {
      setError("Revenue Goal and Profit Goal must be valid numbers");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const monthIdx = months.indexOf(month) + 1;
      const pendingEvent = editMonthForm.eventInput.trim();
      const finalEvents = pendingEvent && !editMonthForm.events.includes(pendingEvent)
        ? [...editMonthForm.events, pendingEvent]
        : editMonthForm.events;
      
      await upsertMonthlyPerformance(
        Number(selectedYear),
        monthIdx,
        0,
        0,
        revenueGoalNum,
        profitGoalNum,
        editMonthForm.leagues,
        finalEvents,
      );
      setEditMonth(null);
      await loadData(selectedYear);
    } catch (e: any) {
      setError(e.message || "Failed to save month goals");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelMonthEdit = () => {
    setEditMonth(null);
  };

  const getMonthKey = (month: string): string => {
    return month.toLowerCase();
  };

  const compareYear = String(Number(selectedYear) - 1);

  // Helper to format profit with sign
  const formatProfit = (profit: number): string => {
    const absProfit = Math.abs(profit).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return profit < 0 ? `-$${absProfit}` : `$${absProfit}`;
  };

  return (
    <div className={`${styles.container} ${isDarkMode ? styles.dark : ""}`}>
      <div className={styles.mainContent}>
        <div className={styles.header}>
          <div>
            <p className={styles.subtitle}>
             Team Goals
            </p>
            <h1 className={styles.title}>{selectedYear} Goals</h1>
          </div>
          <div className={styles.searchSection}>
            <div className={styles.searchWrapper}>
              <SearchIcon className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search by month, league, event..."
                className={styles.searchInput}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <div ref={dashboardDateRef} className={styles.dashboardDateFilterWrapper}>
              <button
                type="button"
                className={`${styles.iconBtn} ${isDashboardDateFilterActive(dashboardDatePeriod) ? styles.dashboardDateBtnActive : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDashboardDatePicker((open) => !open);
                }}
                aria-label="Filter goals by date range"
                title={isDashboardDateFilterActive(dashboardDatePeriod) ? `Filtered: ${formatDashboardDateRangeLabel(dashboardDatePeriod, customDashboardStart, customDashboardEnd)}` : 'Filter by date range'}
              >
                <Calendar size={18} />
              </button>
              {showDashboardDatePicker && (
                <div className={styles.dashboardDateDropdown}>
                  <div className={styles.dashboardDatePresetList}>
                    {DASHBOARD_DATE_PRESETS.map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        className={`${styles.dashboardDatePresetBtn} ${dashboardDatePeriod === preset ? styles.dashboardDatePresetBtnActive : ''}`}
                        onClick={() => handleDashboardDatePreset(preset)}
                      >
                        {preset === 'All' ? 'All time' : preset}
                      </button>
                    ))}
                  </div>
                  {dashboardDatePeriod === 'Custom' && (
                    <div className={styles.dashboardDateCustomFields}>
                      <label>
                        Start
                        <input
                          type="date"
                          value={customDashboardStart}
                          onChange={(e) => setCustomDashboardStart(e.target.value)}
                        />
                      </label>
                      <label>
                        End
                        <input
                          type="date"
                          value={customDashboardEnd}
                          onChange={(e) => setCustomDashboardEnd(e.target.value)}
                        />
                      </label>
                      <button
                        type="button"
                        className={styles.dashboardDatePresetBtn}
                        onClick={() => setShowDashboardDatePicker(false)}
                      >
                        Apply
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <button className={styles.iconBtn} onClick={handleRefresh}>
              <RefreshRoundedIcon fontSize="small" />
            </button>
            <button className={styles.iconBtn} onClick={() => setIsDarkMode(!isDarkMode)}>
              {isDarkMode ? (
                <LightModeOutlinedIcon fontSize="small" />
              ) : (
                <DarkModeOutlinedIcon fontSize="small" />
              )}
            </button>
          </div>
        </div>
        {dashboardDateFilterActive && (
          <div className={styles.dashboardDateBanner}>
            <span>{dashboardDateLabel}</span>
            <button
              type="button"
              className={styles.dashboardDateClearLink}
              onClick={handleClearDashboardDateFilter}
            >
              Clear filter
            </button>
          </div>
        )}
        
        <div className={styles.yearSelector}>
          <span className={styles.yearLabel}>Year:</span>
          <div className={styles.yearDropdown} onClick={() => setShowYearDropdown(!showYearDropdown)}>
            <span>{selectedYear}</span>
            <ArrowDropDownIcon />
            {showYearDropdown && (
              <div className={styles.dropdownMenu}>
                {[2026, 2025, 2024, 2023, 2022].map(y => (
                  <div key={y} className={styles.dropdownItem} onClick={() => { setSelectedYear(String(y)); setShowYearDropdown(false); }}>{y}</div>
                ))}
              </div>
            )}
          </div>
          <button className={styles.refreshBtn} onClick={handleRefresh}>
            <RefreshRoundedIcon /> Refresh
          </button>
        </div>
        
        {/* Annual Goals Card */}
        <div className={styles.annualGoalsCard}>
          <div className={styles.annualGoalsHeader}>
            <h3 className={styles.annualGoalsTitle}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgb(93 117 248)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="12" r="6"></circle>
                <circle cx="12" cy="12" r="2"></circle>
              </svg>&nbsp;
              {selectedYear} Annual Goals
            </h3>
          
              <button className={styles.editAnnualBtn} onClick={() => setShowEditGoalsPopup(true)}>
                <EditIcon className={styles.editIcon} />
                Edit Goals
              </button>
          
          </div>
          
          <div className={styles.annualGoalsGrid}>
            {/* Revenue Goal */}
            <div className={styles.annualGoalItem}>
              <div className={styles.annualGoalContent}>
                <div className={styles.annualGoalHeader}>
                  <span className={styles.annualGoalLabel}>Total Revenue</span>
                  <span className={Number(annualGoals.revenuePercentage) > 0 ? styles.pctPositive : styles.pctNegative}>
                    {annualGoals.revenuePercentage}% of Goal
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span className={styles.annualGoalValue}>${annualGoals.revenueAchieved}</span>
                  <span className={styles.goalDivider}>/ ${annualGoals.totalRevenue}</span>
                </div>
                <div className={styles.progressBar}>
                  <div 
                    className={styles.progressFill} 
                    style={{ 
                      width: `${Math.min(100, Number(annualGoals.revenuePercentage))}%`,
                      background: getProgressBarColor(Number(annualGoals.revenuePercentage), 'revenue')
                    }} 
                  />
                </div>
                {prevAnnual !== null && (
                  <span className={styles.yoyText}>
                    {compareYear}: ${prevAnnual.revenue.toLocaleString('en-US')}{' '}
                    {prevAnnual.revenue > 0 && (() => {
                      const cur = Number(annualGoals.revenueAchieved.replace(/,/g, '')) || 0;
                      const rawDiff = Math.abs(((cur - prevAnnual.revenue) / prevAnnual.revenue) * 100);
                      const diff = Math.min(rawDiff, 100).toFixed(1);
                      return cur >= prevAnnual.revenue
                        ? <span className={styles.yoyPositive}>▲ +{diff}%</span>
                        : <span className={styles.yoyNegative}>▼ -{diff}%</span>;
                    })()}
                  </span>
                )}
              </div>
            </div>

            {/* Profit Goal - Shows actual profit (can be negative) */}
            <div className={styles.annualGoalItem}>
              <div className={styles.annualGoalContent}>
                <div className={styles.annualGoalHeader}>
                  <span className={styles.annualGoalLabel}>QA Gross Profit</span>
                  <span className={Number(annualGoals.profitPercentage) >= 0 ? styles.pctPositive : styles.pctNegative}>
                    {Number(annualGoals.profitPercentage) >= 0 ? `${annualGoals.profitPercentage}% of Goal` : 'Below Goal'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span 
                    className={styles.annualGoalValue} 
                    style={{ color: Number(annualGoals.profitAchieved) >= 0 ? 'rgb(5,150,105)' : '#dc2626' }}
                  >
                    {formatProfit(Number(annualGoals.profitAchieved.replace(/,/g, '')))}
                  </span>
                  <span className={styles.goalDivider}>/ ${annualGoals.qaProfit}</span>
                </div>
                <div className={styles.progressBar}>
                  <div 
                    className={styles.progressFill} 
                    style={{ 
                      width: `${Math.min(100, Math.max(0, Number(annualGoals.profitPercentage)))}%`,
                      background: getProgressBarColor(Math.max(0, Number(annualGoals.profitPercentage)), 'profit')
                    }} 
                  />
                </div>
                {prevAnnual !== null && (
                  <span className={styles.yoyText}>
                    {compareYear}: {formatProfit(prevAnnual.profit)}{' '}
                    {prevAnnual.profit !== 0 && (() => {
                      const cur = Number(annualGoals.profitAchieved.replace(/,/g, '')) || 0;
                      const rawDiff = Math.abs(((cur - prevAnnual.profit) / Math.abs(prevAnnual.profit)) * 100);
                      const diff = Math.min(rawDiff, 100).toFixed(1);
                      return cur >= prevAnnual.profit
                        ? <span className={styles.yoyPositive}>▲ +{diff}%</span>
                        : <span className={styles.yoyNegative}>▼ -{diff}%</span>;
                    })()}
                  </span>
                )}
              </div>
            </div>

            <div className={styles.annualGoalItem}>
              <div className={styles.annualGoalContent}>
                <div className={styles.annualGoalHeader}>
                  <span className={styles.annualGoalLabel}>Sold Tickets</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span className={styles.annualGoalValue}>{annualGoals.ticketsAchieved}</span>
                  <span className={styles.goalDivider}>tickets sold</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Annual financial breakdown (BP spec) */}
        <div className={styles.breakdownCard}>
          <h3 className={styles.breakdownTitle}>{selectedYear} Financial Breakdown</h3>
          <div className={styles.breakdownGrid}>
            {[
              { label: 'Total ticket sales', value: annualBreakdown.totalSale },
              { label: 'User payout', value: annualBreakdown.userPayout },
              { label: 'VL fees', value: annualBreakdown.vlFee },
              { label: 'QA gross (before rep/affiliate)', value: annualBreakdown.qaGross },
              { label: 'Sales rep commission', value: annualBreakdown.salesRepCommission },
              { label: 'Direct affiliate', value: annualBreakdown.directAffiliate },
              { label: 'Indirect affiliate', value: annualBreakdown.indirectAffiliate },
              { label: 'QA net (after rep/affiliate)', value: annualBreakdown.qaNet, highlight: true },
            ].map((row) => (
              <div key={row.label} className={`${styles.breakdownItem} ${row.highlight ? styles.breakdownItemHighlight : ''}`}>
                <span className={styles.breakdownLabel}>{row.label}</span>
                <span className={styles.breakdownValue}>{formatBreakdownCurrency(row.value)}</span>
              </div>
            ))}
          </div>
          {prevAnnualBreakdown.totalSale > 0 && (
            <p className={styles.breakdownYoY}>
              {compareYear} QA net: {formatBreakdownCurrency(prevAnnualBreakdown.qaNet)}
            </p>
          )}
        </div>

        {/* Monthly Breakdown Title */}
        <h2 className={styles.monthlyTitle}>
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgb(93 117 248)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 2v4"></path>
            <path d="M16 2v4"></path>
            <rect width="18" height="18" x="3" y="4" rx="2"></rect>
            <path d="M3 10h18"></path>
            <path d="M8 14h.01"></path>
            <path d="M12 14h.01"></path>
            <path d="M16 14h.01"></path>
            <path d="M8 18h.01"></path>
            <path d="M12 18h.01"></path>
            <path d="M16 18h.01"></path>
          </svg>
          Monthly Breakdown
        </h2>

        {/* Monthly Grid */}
        <div className={styles.monthlyGrid}>
          {getFilteredMonths().map((month) => {
            const monthKey = getMonthKey(month);
            const data = monthlyData[monthKey];
            if (!data) return null;
            const isEditing = editMonth === month;
            const annualRevenueGoal = toNumber(annualGoals.totalRevenue.replace(/,/g, ''));
            const annualProfitGoal = toNumber(annualGoals.qaProfit.replace(/,/g, ''));
            const monthRevenue = toNumber(data.revenue);
            const monthProfit = toNumber(data.profit);
            const monthGoal = getMonthlyGoal(toNumber(data.goal), annualRevenueGoal);
            const monthProfitGoal = getMonthlyGoal(toNumber(data.profitGoal), annualProfitGoal);
            const monthPercentage = monthGoal > 0 ? getPercentage(monthRevenue, monthGoal) : 0;
            const monthPercentageDisplay = monthPercentage.toFixed(1);
            const revenueStatus = monthGoal > 0 ? getPerformanceStatus(monthRevenue, monthGoal) : 'no-goal';
            const profitStatus = monthProfitGoal > 0 ? getPerformanceStatus(monthProfit, monthProfitGoal) : 'no-goal';
            const isGoalExceeded = monthGoal > 0 && monthRevenue >= monthGoal;
            
            return (
              <div key={month} className={styles.monthCard}>
                <div
                  className={styles.monthHeader}
                  style={isGoalExceeded ? { background: 'rgb(5, 150, 105)' } : {}}
                >
                  <div>
                    <h3 className={styles.monthTitle}>{month}</h3>
                    <div className={styles.monthStats}>
                      {data.leagues.length} leagues • {data.events.length} major events
                    </div>
                  </div>
                  <div className={styles.monthGoalPercentage}>
                    {monthPercentageDisplay}%
                    <span>{monthGoal > 0 ? 'of goal' : 'no goal set'}</span>
                  </div>
                </div>
                <div className={styles.monthContent}>
                  {!isEditing ? (
                    <>
                      <div className={styles.metricsRow}>
                        <div className={styles.metricItem}>
                          <span className={styles.metricLabel}>REVENUE</span>
                          <span className={styles.metricValue} style={{ color: getColor(revenueStatus) }}>
                            ${monthRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <span className={styles.metricGoal}>Goal: ${monthGoal.toLocaleString('en-US')}</span>
                        </div>
                        <div className={styles.metricItem}>
                          <span className={styles.metricLabel}>QA GROSS</span>
                          <span 
                            className={styles.metricValue} 
                            style={{ color: monthProfit >= 0 ? getColor(profitStatus) : '#dc2626' }}
                          >
                            {formatProfit(monthProfit)}
                          </span>
                          <span className={styles.metricGoal}>Goal: ${monthProfitGoal.toLocaleString('en-US')}</span>
                          {(() => {
                            const monthNum = MONTH_KEYS.indexOf(monthKey) + 1;
                            const net = monthlyBreakdownStats.find((m) => m.month === monthNum)?.qaNet || 0;
                            return net > 0 ? (
                              <span className={styles.metricSub}>Net: {formatBreakdownCurrency(net)}</span>
                            ) : null;
                          })()}
                        </div>
                      </div>
                      <div className={styles.metricGoal} style={{ marginBottom: 12 }}>
                        Tickets sold: {data.tickets}
                      </div>
                      
                      {/* YoY Comparison */}
                      {(() => {
                        const prev = prevMonthlyData[monthKey];
                        if (!prev) return null;
                        const prevRev = toNumber(prev.revenue);
                        const prevProfit = toNumber(prev.profit);
                        const revChange = getYoYChange(monthRevenue, prevRev);
                        const profitChange = getYoYChange(monthProfit, prevProfit);
                        const badgeStyle = (type: 'profit' | 'loss' | 'neutral') => ({
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 2,
                          fontSize: 11,
                          fontWeight: 700,
                          padding: '2px 7px',
                          borderRadius: 6,
                          background: isDarkMode
                            ? type === 'profit'
                              ? 'rgba(16, 185, 129, 0.16)'
                              : type === 'loss'
                                ? 'rgba(239, 68, 68, 0.16)'
                                : 'rgba(148, 163, 184, 0.18)'
                            : type === 'profit'
                              ? '#dcfce7'
                              : type === 'loss'
                                ? '#fee2e2'
                                : '#f3f4f6',
                          color: type === 'profit' ? '#16a34a' : type === 'loss' ? '#dc2626' : '#6b7280',
                        });
                        const arrow = (type: 'profit' | 'loss' | 'neutral') =>
                          type === 'profit' ? '↑' : type === 'loss' ? '↓' : '–';
                        return (
                          <div className={styles.comparisonSection}>
                            <div className={styles.comparisonHeader}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{compareYear} Comparison</span>
                              <div style={{ display: 'flex', gap: 5 }}>
                                <span style={badgeStyle(revChange.type)}>
                                  {arrow(revChange.type)} {revChange.type === 'profit' ? '+' : revChange.type === 'loss' ? '-' : ''}{revChange.percentage}% Rev
                                </span>
                                <span style={badgeStyle(profitChange.type)}>
                                  {arrow(profitChange.type)} {profitChange.type === 'profit' ? '+' : profitChange.type === 'loss' ? '-' : ''}{profitChange.percentage}% Profit
                                </span>
                              </div>
                            </div>
                            <div className={styles.comparisonContent}>
                              <div className={styles.comparisonItem}>
                                <span className={styles.compLabel}>Revenue</span>
                                <span className={styles.compValue}>${prevRev.toLocaleString('en-US')}</span>
                              </div>
                              <div className={styles.comparisonItem}>
                                <span className={styles.compLabel}>Profit</span>
                                <span className={styles.compValue} style={{ color: prevProfit >= 0 ? '#16a34a' : '#dc2626' }}>
                                  {formatProfit(prevProfit)}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      
                      <div className={styles.leaguesSection} style={{ marginBottom: 8 }}>
                        <h4 className={styles.sectionSubtitle}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"></path>
                          </svg>
                          Active Leagues
                        </h4>
                        {data.leagues && data.leagues.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {data.leagues.map((league, idx) => (
                              <span key={league} style={{
                                background: getLeagueColor(league, idx),
                                color: '#fff',
                                borderRadius: 12,
                                padding: '2px 10px',
                                fontSize: 12,
                                fontWeight: 500,
                              }}>{league}</span>
                            ))}
                          </div>
                        ) : (
                          <p className={styles.noData}>No leagues set</p>
                        )}
                      </div>
                      
                      {/* Major Events Section - List View */}
                      <div className={styles.eventsSection}>
                        <h4 className={styles.sectionSubtitle}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M8 2v4"></path>
                            <path d="M16 2v4"></path>
                            <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                            <path d="M3 10h18"></path>
                          </svg> &nbsp;
                          Major Events
                        </h4>
                        {data.events && data.events.length > 0 ? (
                          <ul className={styles.eventsList}>
                            {data.events.map((event, idx) => (
                              <li key={idx} className={styles.eventListItem}>
                                <span className={styles.eventBullet}>•</span>
                                <span className={styles.eventName}>{event}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className={styles.noData}>No events set</p>
                        )}
                      </div>
                      
                   
                        <button className={styles.editMonthBtn} onClick={() => handleEditMonth(month)}>
                          Edit {month} Goals
                        </button>
                  
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 11, color: '#6366f1', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>REVENUE GOAL ($)</div>
                          <input
                            className={styles.metricInput}
                            name="revenueGoal"
                            type="number"
                            min="0"
                            value={editMonthForm.revenueGoal}
                            onChange={handleEditMonthChange}
                            placeholder="e.g. 100000"
                            style={{ width: '100%' }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 11, color: '#6366f1', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>PROFIT GOAL ($)</div>
                          <input
                            className={styles.metricInput}
                            name="profitGoal"
                            type="number"
                            min="0"
                            value={editMonthForm.profitGoal}
                            onChange={handleEditMonthChange}
                            placeholder="e.g. 10000"
                            style={{ width: '100%' }}
                          />
                        </div>
                      </div>
                      
                      <div className={styles.leaguesSection} style={{ marginBottom: 8 }}>
                        <h4 className={styles.sectionSubtitle}>Active Leagues</h4>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingBottom: 2 }}>
                          {leagues.map((league, idx) => {
                            const checked = editMonthForm.leagues.includes(league);
                            const color = getLeagueColor(league, idx);
                            return (
                              <label
                                key={league}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                  background: color,
                                  color: '#fff',
                                  borderRadius: 12,
                                  padding: '3px 10px',
                                  fontWeight: 500,
                                  fontSize: 12,
                                  border: checked ? '2px solid #fff' : '2px solid transparent',
                                  outline: checked ? `2px solid ${color}` : 'none',
                                  userSelect: 'none',
                                  transition: 'all 0.15s',
                                  boxShadow: checked ? `0 0 0 2px ${color}` : 'none',
                                  whiteSpace: 'nowrap',
                                  opacity: checked ? 1 : 0.6,
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => handleEditMonthLeagueToggle(league)}
                                  style={{ display: 'none' }}
                                />
                                {checked && (
                                  <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 14,
                                    height: 14,
                                    background: 'rgba(255,255,255,0.9)',
                                    borderRadius: '50%',
                                    marginRight: 5,
                                    fontSize: 9,
                                    color: color,
                                    fontWeight: 700,
                                    flexShrink: 0,
                                  }}>✓</span>
                                )}
                                <span>{league}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Edit Mode Events Section - List View */}
                      <div className={styles.eventsSection}>
                        <h4 className={styles.sectionSubtitle}>Major Events</h4>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                          <input
                            className={styles.metricInput}
                            name="eventInput"
                            type="text"
                            value={editMonthForm.eventInput}
                            onChange={handleEditMonthChange}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddEvent(); } }}
                            placeholder="Add event..."
                            style={{ flex: 1 }}
                          />
                          <button type="button" className={styles.saveBtn} style={{ padding: '4px 12px', minWidth: 0 }} onClick={handleAddEvent}>+ Add</button>
                        </div>
                        {editMonthForm.events.length > 0 && (
                          <ul className={styles.eventsList}>
                            {editMonthForm.events.map((event, idx) => (
                              <li key={idx} className={styles.eventListItem}>
                                <span className={styles.eventBullet}>•</span>
                                <span className={styles.eventName}>{event}</span>
                                <button
                                  className={styles.removeEventBtn}
                                  onClick={() => handleRemoveEvent(event)}
                                  aria-label="Remove event"
                                >
                                  ×
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      
                      <div className={styles.editMonthActions}>
                        <button className={styles.cancelBtn} onClick={handleCancelMonthEdit}>Cancel</button>
                        <button className={styles.saveBtn} onClick={() => handleSaveMonthGoals(month)}>Save</button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty Search Results */}
        {searchText.trim() && getFilteredMonths().length === 0 && (
          <div className={styles.emptyResults}>
            <p>No results found for "{searchText}"</p>
            <p>Try searching by month name, league, or event</p>
          </div>
        )}
      </div>

      {/* Edit Annual Goals Popup */}
      {showEditGoalsPopup  && (
        <div className={styles.popupOverlay}>
          <div className={styles.popup} style={{ maxWidth: 560, width: '95vw' }}>
            <div className={styles.popupHeader}>
              <h3>Edit {selectedYear} Annual Goals</h3>
              <button className={styles.closeBtn} onClick={() => setShowEditGoalsPopup(false)}>
                <CloseIcon />
              </button>
            </div>
            <div className={styles.popupContent}>
              <div className={styles.popupField}>
                <label>Total Revenue Goal ($)</label>
                <input
                  type="text"
                  value={annualGoals.totalRevenue}
                  onChange={(e) => setAnnualGoals({ ...annualGoals, totalRevenue: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div className={styles.popupField}>
                <label>QA Profit Goal ($)</label>
                <input
                  type="text"
                  value={annualGoals.qaProfit}
                  onChange={(e) => setAnnualGoals({ ...annualGoals, qaProfit: e.target.value })}
                  placeholder="0"
                />
              </div>
            </div>
            <div className={styles.popupActions}>
              <button className={styles.cancelBtn} onClick={() => setShowEditGoalsPopup(false)}>Cancel</button>
              <button className={styles.saveBtn} onClick={handleSaveAnnualGoals}>Save Goals</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Goals;